import { Box, Button, Card, Heading, Icon, Paragraph } from '@zillow/constellation-10';
import { IconBuildingsFilled, IconCircleFilled, IconHammerFilled, IconTagOutline } from '@zillow/constellation-icons';
import { MarkerIcon } from '../../SharedComponents/Map/Dots/IconMapDot';
import type { CompetitorMapDisplayMode } from '../../SharedComponents/Map/Dots/Dots';
import { ClientAdProductType } from '@zg-rentals/ts-customer-analytics-client';
import { OTHER_ADVERTISING_COLOR, PACKAGE_COLORS } from '../../SharedComponents/Map/Dots/Dots.consts';
import { mapPackageTypeName } from '../../../utils/mapPackageTypeName';

type MapLegendProps = {
  displayMode: CompetitorMapDisplayMode;
  showAllComps: boolean;
  showNewConstruction: boolean;
  onRecenter: () => void;
  visiblePackageTypes: Array<ClientAdProductType>;
  hasOtherAdvertising: boolean;
};

const PACKAGES_ORDER: Partial<Record<ClientAdProductType, number>> = {
  [ClientAdProductType.BASE_PACKAGE]: 1,
  [ClientAdProductType.PREMIUM_PACKAGE]: 2,
  [ClientAdProductType.ENHANCED_PACKAGE]: 3,
  [ClientAdProductType.SIGNATURE_PACKAGE]: 4,
  [ClientAdProductType.INTEGRATED_PAY_PER_LEASE]: 5,
};

const TearDropMarker = ({ backgroundColor, label }: { backgroundColor: string; label: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 80" width="24" height="32">
    <ellipse cx="30" cy="75" rx="8" ry="3" fill="rgba(0,0,0,0.3)" />
    <path
      d="M 30 5 C 15 5, 5 15, 5 30 C 5 40, 10 50, 30 75 C 50 50, 55 40, 55 30 C 55 15, 45 5, 30 5 Z"
      fill={backgroundColor}
      stroke="#111116"
      strokeWidth="2"
    />
    <text
      x="30"
      y="32"
      textAnchor="middle"
      dominantBaseline="middle"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      fontSize="17"
      fontWeight="700"
      fill="#FFFFFF"
    >
      {label}
    </text>
  </svg>
);

const LegendRow = ({ marker, label }: { marker: React.ReactNode; label: string }) => (
  <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
    <Box css={{ flexShrink: 0, width: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {marker}
    </Box>
    <Paragraph css={{ fontSize: '13px' }}>{label}</Paragraph>
  </Box>
);

export const MapLegend = ({
  displayMode,
  showAllComps,
  showNewConstruction,
  onRecenter,
  visiblePackageTypes,
  hasOtherAdvertising,
}: MapLegendProps) => {
  return (
    <Card
      css={{
        position: 'absolute',
        top: '32px',
        right: '32px',
        padding: '400',
        zIndex: 10,
        pointerEvents: 'auto',
        width: '240px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
        <Box css={{ marginBottom: '100' }}>
          <Heading level={5}>Legend</Heading>
        </Box>
        <LegendRow
          marker={
            <MarkerIcon
              icon={<IconBuildingsFilled />}
              backgroundColor="#0041D9"
              iconColor="#FFFFFF"
              width={40}
              height={43}
            />
          }
          label="Your property"
        />

        {displayMode === 'default' && (
          <>
            <LegendRow
              marker={
                <MarkerIcon
                  icon={<IconBuildingsFilled />}
                  backgroundColor="#21A495"
                  iconColor="#111116"
                  width={40}
                  height={43}
                />
              }
              label="Selected competitors"
            />
          </>
        )}

        {displayMode === 'specialOffer' && (
          <LegendRow
            marker={
              <MarkerIcon
                icon={<IconTagOutline />}
                backgroundColor="#22C55E"
                iconColor="#FFFFFF"
                width={40}
                height={43}
              />
            }
            label="Competitor with special offer"
          />
        )}

        {(displayMode === 'lowPrice' || displayMode === 'highPrice') && (
          <>
            <LegendRow marker={<TearDropMarker backgroundColor="#21A495" label="$2K" />} label="Selected competitors" />
            <LegendRow marker={<TearDropMarker backgroundColor="#999999" label="$2K" />} label="Competitors" />
          </>
        )}

        {(displayMode === 'viewsRank' || displayMode === 'leadsRank') && (
          <>
            <LegendRow marker={<TearDropMarker backgroundColor="#21A495" label="1" />} label="Selected competitors" />
            <LegendRow marker={<TearDropMarker backgroundColor="#999999" label="2" />} label="Competitors" />
          </>
        )}

        {showNewConstruction && (
          <LegendRow
            marker={
              <MarkerIcon
                icon={<IconHammerFilled />}
                backgroundColor="#111116"
                iconColor="#FFFFFF"
                width={40}
                height={43}
              />
            }
            label="Under construction / Planned"
          />
        )}

        {(visiblePackageTypes.length > 0 || hasOtherAdvertising) && displayMode === 'default' && (
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '200', marginTop: '200' }}>
            <Paragraph css={{ fontSize: '12px', fontWeight: 600 }}>Advertising</Paragraph>
            {visiblePackageTypes
              .sort((p1, p2) => {
                const o1 = PACKAGES_ORDER[p1] || -1;
                const o2 = PACKAGES_ORDER[p2] || -1;

                return o1 - o2;
              })
              .map((packageType) => (
                <LegendRow
                  key={packageType}
                  marker={
                    <svg width="14" height="14" viewBox="0 0 14 14">
                      <circle cx="7" cy="7" r="7" fill={PACKAGE_COLORS[packageType]} />
                    </svg>
                  }
                  label={mapPackageTypeName(packageType)}
                />
              ))}
            {hasOtherAdvertising && (
              <LegendRow
                marker={
                  <svg width="14" height="14" viewBox="0 0 14 14">
                    <circle cx="7" cy="7" r="7" fill={OTHER_ADVERTISING_COLOR} />
                  </svg>
                }
                label="Other"
              />
            )}
            {showAllComps && (
              <LegendRow
                marker={
                  <Icon size="sm" css={{ color: 'gray.500' }}>
                    <IconCircleFilled />
                  </Icon>
                }
                label="None"
              />
            )}
          </Box>
        )}

        <Button emphasis="outlined" size="sm" onClick={onRecenter} css={{ width: '100%', marginTop: '300' }}>
          Recenter
        </Button>
      </Box>
    </Card>
  );
};
